import React, { useState, useCallback, useEffect, ReactElement, useMemo } from 'react';
import { Customer, SpecData, ViewApptList, ViewAppt, UpdateApptDateInp, UpdateApptOpsInp, ServiceDocsData, TokenKeys, RepairOrder, Address, ROHistoryData, ROHistory, ascPageviewInput, ascCtaInteractionInput } from 'src/models';
import { strings } from 'src/localization';
import { IView } from 'Framework';
import { ComponentMapIds } from '../map';
import { ViewMode, CustMode, Vehicles } from 'src/enums';
import css from './UserAccountMenu.module.scss';
// import contactCSS from '../ContactDealer/ContactDealer.module.scss';
import UserAccountPanel from './UserAccountPanel';
import { Dialog, LoadingProgress, ScrollBar } from '@framework-controls/core';
import ContactDealer from '../ContactDealer/ContactDealer';
import { IRepairOrdersState, IVehicleState } from 'src/store/states';
import { THEME } from 'src/constants/ThemeNames';
import { DateTime } from '@reynolds/datetime';
import { ascCtaInteraction, ascPageview, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

export interface UserAccountLink {
    id: string;
    label: string;
    componentId: ComponentMapIds;
    group: 'Appt' | 'Account' | 'Vehicle' | 'Dealer';
    disabled?: boolean;
    custModeType: CustMode,
    hide?: boolean;
}

export interface IProps {
    customerInfo?: Customer;
    specInfo?: SpecData,
    theme: string,
    repairOrders: IRepairOrdersState,
    roHistory: ROHistoryData,
    vehicles: IVehicleState,
    address: Address,
    clearMainPanes: () => void;
    pushToMainPanes: (inputView: IView) => void;
    setViewMode: (viewMode: ViewMode) => void;
    mainPaneViewOrder: IView[];
    viewApptList?: ViewApptList;
    loadingROs: boolean;
    selectVehicle: (id: string) => void;
    getCustAppts: (custID: string, profile: string, store: string, branch: string) => void;
    setCustMode: (custMode: CustMode) => void;
    selectedVehicle?: string;
    serviceDocsSpecs?: ServiceDocsData;
    newTabUrl?: string;
    onCustUpdate: (cust: Customer) => void;
    popFromMainPane: (id: string) => void;
    getVehicles: () => void;
    clearCustInfo: () => void;
    clearVehicles: () => void;
    clearNewVehicle: () => void;
    clearApptState: () => void;
    clearROState: () => void;
    clearROHistory: () => void;
    clearMenusState: () => void;
    waitForLogOff: () => void;
    logCustOff: () => void;
    profileNum: string;
    store: string;
    branch: string;
    setVehNextAppt: (vehicleIndex: string, apptID: string) => void;
    fetchROs: (custNum: string) => void;
    selectRO: (roNum: string) => void;
    updateApptOps: (ops: UpdateApptOpsInp[]) => void;
    updateApptDate: (date: UpdateApptDateInp) => void;
    generateUrl: (tokenData: TokenKeys) => void;
    generateDocsUrl: (tokenData: TokenKeys) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    clearNewTabUrl: () => void;
    locale: string;
    lexusMCLaunched: string,
    toyotaMCLaunched: string,
    employeeFlag?: boolean,
    gmRewardsEligible: string | undefined,
    gmMemberErr: string,
    spmCustomerCentral: (cust: Customer) => void;
    oemCustSearchLaunched: boolean;
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    countryCode: string;
    viewVehicles: (inp: Vehicles) => void;
    setLoadingStateToIdle: () => void;
    clearError: () => void;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    identityVerified: boolean;
}

const UserAccountMenu = (props: IProps) => {
    const accountLinks: UserAccountLink[] = useMemo(() => [
        {
            id: 'viewRepair', label: strings.ViewCurrentRepair, componentId: ComponentMapIds.OpenRepairSelect,
            group: 'Appt', custModeType: CustMode.ViewCurrentRepair, disabled: false, hide: false,
        },
        {
            id: 'viewAppt', label: strings.ViewCurrentAppt, componentId: ComponentMapIds.ViewAppt,
            group: 'Appt', custModeType: CustMode.ViewCurrentAppt, disabled: false, hide: false,
        },
        {
            id: 'addAppt', label: strings.AddNewAppt, componentId: ComponentMapIds.VehSel,
            group: 'Appt', custModeType: CustMode.AddNewAppt, disabled: false, hide: false,
        },
        {
            id: 'viewAcct', label: strings.ViewAcctInfo, componentId: ComponentMapIds.ViewAccount,
            group: 'Account', custModeType: CustMode.ViewAcctinfo, disabled: false, hide: false,
        },
        {
            id: 'viewDocument', label: strings.ViewDocuments, componentId: ComponentMapIds.ServiceDocs,
            group: 'Account', custModeType: CustMode.ViewDocs, disabled: true, hide: false,
        },
        {
            id: 'changePwd', label: strings.ChangePassword, componentId: ComponentMapIds.ChangePassword,
            group: 'Account', custModeType: CustMode.ChangePass, disabled: false, hide: false,
        },
        {
            id: 'logout', label: strings.LogOut, componentId: ComponentMapIds.Login,
            group: 'Account', custModeType: CustMode.Nothing, disabled: false, hide: false,
        },
        {
            id: 'viewVehicle', label: strings.ViewVehicles, componentId: ComponentMapIds.VehSel,
            group: 'Vehicle', custModeType: CustMode.ViewVeh, disabled: false, hide: false,
        },
        {
            id: 'checkRecall', label: strings.CheckMfgRecalls, componentId: ComponentMapIds.VehSel,
            group: 'Vehicle', custModeType: CustMode.CheckRecalls, disabled: false, hide: false,
        },
        {
            id: 'checkMaintContract', label: strings.CheckMaintContract, componentId: ComponentMapIds.VehSel,
            group: 'Vehicle', custModeType: CustMode.CheckMaintContract, disabled: false, hide: false,
        },
        {
            id: 'viewRecommend', label: strings.ViewRecommended, componentId: ComponentMapIds.VehSel,
            group: 'Vehicle', custModeType: CustMode.ViewRecSvcs, disabled: false, hide: false,
        },
        {
            id: 'viewVehHist', label: strings.ViewRepairHist, componentId: ComponentMapIds.VehSel,
            group: 'Vehicle', custModeType: CustMode.ViewHistory, disabled: false, hide: false,
        },
        {
            id: 'addVehicle', label: strings.AddVehicle, componentId: ComponentMapIds.AddCustVehicle,
            group: 'Vehicle', custModeType: CustMode.AddVeh, disabled: false, hide: false,
        },
        {
            id: 'contact', label: strings.ContactDealer, componentId: ComponentMapIds.ContactDealer,
            group: 'Dealer', custModeType: CustMode.ContactDealer, disabled: false, hide: false,
        },
        {
            id: 'locale', label: props.locale, componentId: ComponentMapIds.OpenRepairSelect,
            group: 'Appt', custModeType: CustMode.ViewCurrentRepair, disabled: false, hide: true,
        },

    ], [props.locale]);

    const [isOpen, setIsOpen] = useState(false);
    const [gettingAppts, setGettingAppts] = useState(false);
    const [updateLink, setUpdateLink] = useState(false);
    const [acctLinks, setAcctLinks] = useState(accountLinks.filter((x) => x.group === 'Account'));
    const [vehLinks, setVehLinks] = useState(accountLinks.filter((x) => x.group === 'Vehicle'));
    const [apptLinks, setApptLinks] = useState(accountLinks.filter((x) => x.group === 'Appt'));
    const [dlrLinks, setDlrLinks] = useState(accountLinks.filter((x) => x.group === 'Dealer'));
    const [generatingURL, setGeneratingURL] = useState(false);
    const availableAppts: ViewAppt[] = useMemo(() => [], []);
    const [availableAppt, setAvailableAppt] = useState(availableAppts);
    const [spmCallHasRun, setSpmCallHasRun] = useState(false);
    const [custModeType, setCustModeType] = useState(CustMode.AddNewAppt);
    const [searchingCustomer, setSearchingCustomer] = useState(false);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'UserAccountMenu', '');
    }, [
        props.addLogEntry,
    ]);

    // This logic is just to force a rebuild if locale changes.  If should not really do anything.
    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);

        }
    }, [props.locale]);

    const { customerInfo, mainPaneViewOrder } = props;
    const lastPane = mainPaneViewOrder[mainPaneViewOrder.length - 1];
    // Fetch the customer's ROs if we haven't already - currentRepairOrder will be undefined.
    useEffect(() => {
        if (!props.loadingROs && props.customerInfo && props.customerInfo.nameId && !props.repairOrders.currentRepairOrder &&
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].component === ComponentMapIds.UserAccountMenu) {
            if (lastPane.viewId !== 'VehLayout') {
                props.fetchROs(props.customerInfo.nameId);
            }
        }
    }, [props.customerInfo, props.repairOrders.currentRepairOrder, props.mainPaneViewOrder,
        lastPane.viewId, props.repairOrders.loadingROs, props.fetchROs, props.loadingROs]);

    if (!props.viewApptList && !gettingAppts && customerInfo && customerInfo.nameId) {
        props.getCustAppts(customerInfo.nameId, props.profileNum, props.store, props.branch);
        setGettingAppts(true);
    }
    if (gettingAppts && props.viewApptList) {
        setGettingAppts(false);
    }
    // Determine the availability of the View / Update account information link.
    useEffect(() => {
        if (props.employeeFlag) {
            const viewAccountIndex = accountLinks.findIndex(link => link.id === 'viewAcct');
            accountLinks[viewAccountIndex].disabled = true;
            setUpdateLink(true);
        }
    }, [accountLinks, props.employeeFlag]);

    // Determine the availability of the View Documents link.
    useEffect(() => {
        // if (props.serviceDocsSpecs && props.repairOrders.RepairOrders){
        if (props.serviceDocsSpecs && props.repairOrders.RepairOrders){
            let repairOrders: RepairOrder[] = props.repairOrders.RepairOrders;
            const repairHistory: ROHistory[] = props.roHistory.roHistory;
            // If the customer has selected a vehicle, filter the repair orders and ro history to that vehicle.
            if (props.selectedVehicle && props.selectedVehicle !== 'AddVehicle') {
                const selectedVin = props.vehicles?.vehicles[props.selectedVehicle].vin;
                repairOrders = repairOrders.filter(ro => ro.vin === selectedVin && ro.vin);
            }
            const viewDocumentsIndex = accountLinks.findIndex(link => link.componentId === ComponentMapIds.ServiceDocs);
            // Enable the "View Documents" link if Service Documents is enabled and we have at
            // least one repair order.
            // accountLinks[viewDocumentsIndex].disabled = !props.serviceDocsSpecs.enabled || repairOrders.length === 0;
            if (viewDocumentsIndex !== -1) {
                accountLinks[viewDocumentsIndex].disabled = !props.serviceDocsSpecs.enabled ||
                    (repairOrders.length === 0 && repairHistory.length === 0);
            }
            setUpdateLink(true);
        }
    }, [accountLinks,
        props.serviceDocsSpecs,
        props.repairOrders.RepairOrders,
        props.selectedVehicle,
        props.vehicles,
        props.roHistory]);

    // Determine the availability of the Maintenance Contracts link.
    useEffect(() => {
        const mcLinkIndex = accountLinks.findIndex(link => link.custModeType === CustMode.CheckMaintContract);
        // if (props.locale !== 'en-US' || (!props.toyotaMCLaunched && !props.lexusMCLaunched)) {
        if (props.countryCode !== 'US' ||
            (!props.toyotaMCLaunched && !props.lexusMCLaunched && !props.lexusTDMCLaunched && !props.toyotaTDMCLaunched)) {
            // if neither tracking flag is set then we must hide the link.
            accountLinks[mcLinkIndex].hide = true;
        }
        const maintMake = props.selectedVehicle && props.selectedVehicle !== 'AddVehicle' ?
            props.vehicles.vehicles[props.selectedVehicle].make.toLowerCase().split(' ')[0]:'';
        if (props.selectedVehicle && (props.toyotaMCLaunched || props.toyotaTDMCLaunched)) {
            accountLinks[mcLinkIndex].disabled = maintMake !== 'toyota';
        }
        if (props.selectedVehicle && (props.lexusMCLaunched || props.lexusTDMCLaunched)) {
            accountLinks[mcLinkIndex].disabled = maintMake !== 'lexus';
        }
        if ((!props.selectedVehicle || props.selectedVehicle === 'AddVehicle') &&
        (props.toyotaMCLaunched || props.lexusMCLaunched || props.lexusTDMCLaunched || props.toyotaTDMCLaunched)) {
            accountLinks[mcLinkIndex].disabled = false;
        }

        setUpdateLink(true);
    }, [accountLinks,
        props.countryCode,
        props.selectedVehicle,
        props.vehicles,
        props.toyotaMCLaunched,
        props.lexusMCLaunched,
        props.lexusTDMCLaunched,
        props.toyotaTDMCLaunched]);

    // Determine the availability of the View Recommended Services link.
    useEffect(() => {
        const recomLinkIndex = accountLinks.findIndex(link => link.custModeType === CustMode.ViewRecSvcs);
        // If we are set to not show the previous recommended services, hide the link.
        if (!props.specInfo?.showPrvRecom) {
            accountLinks[recomLinkIndex].disabled = true;
        }
        setUpdateLink(true);
    }, [accountLinks,
        props.specInfo]);

    // Open a new tab to the specified URL if we have one.
    useEffect(() => {
        // put this back in once UTF8 is added to generateDocsURL
        if (generatingURL && props.newTabUrl) {
        // if (generatingURL) {
            let docUrl = props.newTabUrl;
            // let docUrl = '';
            const urlString = window.location;
            const localURL = urlString.host ?? '';
            if (localURL === 'localhost:3000') {
                // const token = docUrl.split('token=');
                // console.log(token[1]);
                // docUrl = 'http://localhost:3000/?token=481CFEF6DF8D42F5B5B77C6B9D23E0C0';
                const localToken = docUrl.split('token').pop();
                docUrl = 'http://localhost:3000/?token'+localToken;
            }
            window.open(docUrl);
            setGeneratingURL(false);
            // clear token data
            props.clearNewTabUrl();
        }
    }, [generatingURL, props.newTabUrl, props.clearNewTabUrl]);

    // need to make sure this shows when clicking the Contact Us link. Which useState controls the modal?
    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.UserAccountMenu &&
            !isOpen && props.vehicles.vehiclesBuilt) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles.vehicles, props.selectedVehicle);
        }
    }, [props.mainPaneViewOrder, isOpen, props.vehicles.vehicles, props.selectedVehicle, props.vehicles.vehiclesBuilt]);

    const clickHandler = (componentId: string, label: string, custModeType: CustMode, linkId: string) => {
        let screen = componentId;
        props.setLoadingStateToIdle();
        props.viewVehicles(Vehicles.NAM);
        // Clear the Appointment and ApptOps upon selecting a menu item to clear any residual data
        props.updateApptOps([]);
        props.updateApptDate({
            selAdv: '',
            selAdvImage: '',
            selAdvName: '',
            selDate: '',
            selTime: '',
            selTrans: '',
            selTransFlag: '',
        });
        props.setCustMode(custModeType);
        const elementColor = getElementColor(linkId, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: label,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'selection_link',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, linkId, props.vehicles.vehicles, props.selectedVehicle);
        if (componentId === ComponentMapIds.ContactDealer) {
            setIsOpen(true);

            return;
        }
        if (componentId === ComponentMapIds.Login) {
            props.clearMainPanes();
            props.pushToMainPanes({
                viewId: ComponentMapIds.LandingPage,
                component: ComponentMapIds.LandingPage,
            });
            props.pushToMainPanes({
                viewId: ComponentMapIds.Login,
                component: ComponentMapIds.Login,
            });
            props.clearVehicles();
            props.clearApptState();
            props.clearCustInfo();
            props.waitForLogOff();
            props.logCustOff();
            props.clearMenusState();
            props.clearROHistory();
            props.clearROState();
            props.clearNewVehicle();
            props.viewVehicles(Vehicles.All);
            setSpmCallHasRun(false);

            return;
        }
        if (componentId === ComponentMapIds.ServiceDocs) {
            // If the 'View Documents' link was clicked, generate the token URL for a new tab.
            let selectedVin = '';
            if (props.selectedVehicle) {
                selectedVin = props.vehicles?.vehicles[props.selectedVehicle].vin;
            } else {
                let selRO: RepairOrder | undefined = undefined;
                if (props.repairOrders.RepairOrders.length > 1) {
                    // this grabs most recent RO
                    const date = Math.max(...props.repairOrders.RepairOrders.map(ro => Date.parse(ro.RODate)));
                    selRO = props.repairOrders.RepairOrders.find(ro => Date.parse(ro.RODate) === date);
                } else {
                    selRO = props.repairOrders.RepairOrders[0];
                }
                const tokenVeh = (props.vehicles.vehicles.find(veh => veh.vin === selRO!.vin));
                if (tokenVeh) {
                    selectedVin = tokenVeh?.vin;
                }
            }
            const tokenData: TokenKeys = {
                keyStore: props.store,
                keyBranch: props.branch,
                keyProfile: props.profileNum,
                // keyProfile: 'ServiceDocs',
                keyNameId: props.customerInfo?.nameId,
                keyVin: selectedVin,
                keyServiceDocsFlag: 'Y',
                keyExpireDays: '60',
            };
            setGeneratingURL(true);
            props.generateDocsUrl(tokenData);

            return;
        }
        if (custModeType === CustMode.ViewCurrentAppt) {
            if (accountLinks[1].componentId === ComponentMapIds.VehLayout) {
                if (props.viewApptList &&
                    (props.selectedVehicle === '' ||
                    props.selectedVehicle === undefined ||
                    props.selectedVehicle === 'AddVehicle')) {
                    // select vehicle id by finding vin in appt first
                    const firstAppointment = availableAppts[0];
                    let vehicle = props.vehicles.vehicles.find(veh => veh.vin === firstAppointment.vin);
                    if (!firstAppointment.vin) {
                        vehicle = props.vehicles.vehicles.find(veh => firstAppointment.make.toUpperCase() ===
                            veh.make.toUpperCase() && firstAppointment.model?.toUpperCase() === veh.model?.toUpperCase() &&
                            firstAppointment.year?.toUpperCase() === veh.year?.toUpperCase());
                    }
                    if (vehicle) {
                        props.selectVehicle(vehicle.id);
                        props.setVehNextAppt(vehicle.id, firstAppointment.apptNum);
                    }
                } else if (props.viewApptList) {
                    // Find the appointment that matches the vehicle selected
                    const vehicle = props.vehicles.vehicles.find(veh => veh.id === props.selectedVehicle);
                    let appt: ViewAppt | undefined = undefined;
                    if (vehicle && vehicle.vin) {
                        appt = props.viewApptList.find(appt => appt.vin === vehicle.vin);
                    } else if (vehicle) {
                        appt = props.viewApptList.find(appt => vehicle.make?.toUpperCase() === appt.make?.toUpperCase() &&
                        vehicle.model?.toUpperCase() === appt.model?.toUpperCase() &&
                        vehicle.year?.toUpperCase() === appt.year?.toUpperCase());
                    }
                    if (appt && vehicle) {
                        props.setVehNextAppt(vehicle.id, appt.apptNum);
                    }
                }
            }
        }
        if (componentId === ComponentMapIds.OpenRepairSelect) {
            props.selectRO('-1');
        }

        if ((custModeType === CustMode.AddNewAppt || custModeType === CustMode.AddVeh) && props.oemCustSearchLaunched) {
            if (!spmCallHasRun) {
                const custInp: Customer = {
                    nameId: props.customerInfo?.nameId ?? '',
                    firstName: props.customerInfo?.firstName ?? '',
                    lastName: props.customerInfo?.lastName ?? '',
                    emailAddress: props.customerInfo!.emailAddress,
                    phoneNumber: '',
                    login: false,
                    prefContact: '',
                    textOptIn: '',
                    emailOptIn: '',
                    streetAddress: '',
                    stateAddress: '',
                    city: '',
                    zipCode: '',
                    addVehNameId: '',
                    gmRedeemAmt: '',
                    encryptFirst: '',
                    encryptLast: '',
                    encryptEmail: '',
                    encryptPhone: '',
                    ascCustId: '',
                };

                setCustModeType(custModeType);
                setSearchingCustomer(true);
                props.spmCustomerCentral(custInp);

                return;
            } else {
                if (custModeType === CustMode.AddVeh && props.vehicles.vehicles.some(v => v.isOEM === 'Y')) {
                    screen = ComponentMapIds.VehSel;
                    props.viewVehicles(Vehicles.OEM);
                }
                if (custModeType === CustMode.AddNewAppt) {
                    props.viewVehicles(Vehicles.All);
                }
            }
        }
        if (custModeType === CustMode.AddNewAppt) {
            props.setViewMode(ViewMode.Appt);
            props.addLogEntry('Appointment Add Process Start', '', '');
        }
        props.pushToMainPanes({
            viewId: screen,
            component: screen,
        });
    };

    useEffect(() => {
        if (props.loading === 'succeeded' && searchingCustomer) {
            props.setLoadingStateToIdle();
            setSearchingCustomer(false);
            let screen = ComponentMapIds.VehSel;
            setSpmCallHasRun(true);

            if (custModeType === CustMode.AddNewAppt) {
                props.setViewMode(ViewMode.Appt);
                props.addLogEntry('Appointment Add Process Start', '', '');
            }

            if (custModeType === CustMode.AddVeh && !props.customerInfo?.vehicleList?.some(v => v.isOEM === 'Y')) {
                screen = ComponentMapIds.AddCustVehicle;
            }
            if (screen === ComponentMapIds.VehSel && custModeType === CustMode.AddVeh) {
                props.viewVehicles(Vehicles.OEM);
            } else if (custModeType === CustMode.AddNewAppt) {
                props.viewVehicles(Vehicles.All);
            }
            props.pushToMainPanes({
                viewId: screen,
                component: screen,
            });

            props.clearError();
        }
    }, [props.loading, props.pushToMainPanes, props.customerInfo?.vehicleList, custModeType, searchingCustomer,
        props.viewVehicles, props.addLogEntry, props.setViewMode, props.setLoadingStateToIdle, props.clearError]);

    const closeHandler = useCallback(() => {
        setIsOpen(false);
    }, []);

    useEffect(() => {
        // filter appointments that are past due
        let tracker = 0;
        if (props.viewApptList?.length){
            props.viewApptList.forEach((appt) => {
                let date = new DateTime(new Date(appt.date + ' ' + appt.time?.slice(0, 5)));
                if (appt.time?.slice(5, 8) === 'pm'){
                    date = date.addHours(12);
                }
                const rightNow = new DateTime(Date.now());
                if (!date.isBefore(rightNow)) {
                    availableAppts[tracker] = appt;
                    setAvailableAppt(availableAppts);
                    tracker = tracker + 1;
                }
            });
        }
        // if no appointments exist for the customer then disable viewappt link
        if (props.viewApptList === undefined || availableAppt?.length === 0 || !Array.isArray(props.viewApptList)) {
            accountLinks[1] = {
                id: 'viewAppt',
                label: strings.ViewCurrentAppt,
                componentId: ComponentMapIds.ViewAppt,
                group: 'Appt',
                disabled: true,
                custModeType: CustMode.ViewCurrentAppt,
            };
            setUpdateLink(true);
        } else {
            if (props.selectedVehicle === '' || props.selectedVehicle === undefined || props.selectedVehicle === 'AddVehicle') {
                // if only one exists then go to appt and select applicable vehicle by VIN, go to VehLayout
                if (availableAppt && availableAppt?.length === 1) {
                    accountLinks[1] = {
                        id: 'viewAppt',
                        label: strings.ViewCurrentAppt,
                        componentId: ComponentMapIds.VehLayout,
                        group: 'Appt',
                        disabled: false,
                        custModeType: CustMode.ViewCurrentAppt,
                    };
                    setUpdateLink(true);
                }

                // if more than one appt go to apptSel screen
                if (availableAppt && availableAppt?.length > 1) {
                    accountLinks[1] = {
                        id: 'viewAppt',
                        label: strings.ViewCurrentAppt,
                        componentId: ComponentMapIds.ApptSel,
                        group: 'Appt',
                        disabled: false,
                        custModeType: CustMode.ViewCurrentAppt,
                    };
                    setUpdateLink(true);
                }
            } else {
                // grab all appts that have selected vin
                const selVehAppts: ViewAppt[] = [];
                let tracker = 0;
                const vehicle = props.vehicles.vehicles.find(veh => veh.id === props.selectedVehicle);
                availableAppt.forEach((appt) => {
                    if (vehicle && vehicle.vin && appt?.vin === vehicle.vin) {
                        selVehAppts[tracker] = appt;
                        tracker = tracker + 1;
                    }
                    if (vehicle && !vehicle.vin && appt.vin === undefined) {
                        if (vehicle.make?.toUpperCase() === appt.make?.toUpperCase() && vehicle.year === appt.year &&
                            vehicle.model?.toUpperCase() === appt.model?.toUpperCase()) {
                            selVehAppts[tracker] = appt;
                            tracker = tracker + 1;
                        }
                    }
                });
                // determine action based on lenght of the appts for the selected vehicle
                if (selVehAppts.length === 0) {
                    accountLinks[1] = {
                        id: 'viewAppt',
                        label: strings.ViewCurrentAppt,
                        componentId: ComponentMapIds.ViewAppt,
                        group: 'Appt',
                        disabled: true,
                        custModeType: CustMode.ViewCurrentAppt,
                    };
                    setUpdateLink(true);
                }
                if (selVehAppts.length === 1) {
                    accountLinks[1] = {
                        id: 'viewAppt',
                        label: strings.ViewCurrentAppt,
                        componentId: ComponentMapIds.VehLayout,
                        group: 'Appt',
                        disabled: false,
                        custModeType: CustMode.ViewCurrentAppt,
                    };
                    setUpdateLink(true);
                }
                // if more than one appt go to apptSel screen
                if (selVehAppts.length > 1) {
                    accountLinks[1] = {
                        id: 'viewAppt',
                        label: strings.ViewCurrentAppt,
                        componentId: ComponentMapIds.ApptSel,
                        group: 'Appt',
                        disabled: false,
                        custModeType: CustMode.ViewCurrentAppt,
                    };
                    setUpdateLink(true);
                }
            }
        }
    }, [accountLinks, availableAppt, availableAppts, props.selectedVehicle, props.vehicles.vehicles, props.viewApptList]);

    useEffect(() => {
        // disable the recalls link if the show OEM Recalls spec is not checked
        // number needs to be updated if accountLinks array is changed
        if (!props.specInfo?.showOEMRecall) {
            accountLinks[8] = {
                id: 'checkRecall',
                label: strings.CheckMfgRecalls,
                componentId: ComponentMapIds.VehSel,
                group: 'Vehicle',
                custModeType: CustMode.CheckRecalls,
                disabled: true,
            };
            setUpdateLink(true);
        }
    }, [accountLinks, props.specInfo]);

    // Since the Vehicle state depends on Customer state, update Vehicle state if vehicleList is updated.
    useEffect(() => {
        if (!props.vehicles.vehiclesBuilt) {
            props.getVehicles();
        }
    }, [props.getVehicles, props.customerInfo?.vehicleList, props.vehicles.vehiclesBuilt]);

    useEffect(() => {
        if (updateLink) {
            setAcctLinks(accountLinks.filter((x) => x.group === 'Account'));
            setVehLinks(accountLinks.filter((x) => x.group === 'Vehicle'));
            setApptLinks(accountLinks.filter((x) => x.group === 'Appt'));
            setDlrLinks(accountLinks.filter((x) => x.group === 'Dealer'));
            setUpdateLink(false);
        }
    }, [accountLinks, updateLink]);

    // Set the current repair link to disabled if we have no repair orders
    // If a vehicle is currently selected then filter the ROs by vin
    const currentRepairIndex = accountLinks.findIndex(link => link.componentId === ComponentMapIds.OpenRepairSelect);
    let repairOrders = props.repairOrders.RepairOrders ?? [];
    const vehicle = props.vehicles.vehicles.find(veh => veh.id === props.vehicles.selectedVehicle);
    if (vehicle) {
        repairOrders = Object.values(repairOrders).filter(ro => ro.vin === vehicle.vin) ?? '';
    }
    accountLinks[currentRepairIndex].disabled = repairOrders?.length === 0;

    const firstName = !customerInfo?.firstName ? '' : customerInfo.firstName;

    let loadingProgress: ReactElement | null = null;
    if (generatingURL) {
        loadingProgress = <LoadingProgress size={100} />;
    }
    const lightBoxHeader = classNames(
        css.LightBoxHeader,
        {
            [css.LightBoxHeaderHy]: props.theme === 'hyundai',
            [css.LightBoxHeaderMazda]: props.theme === 'mazda',
            [css.LightBoxHeaderGMC]: props.theme === 'gmc',
            [css.LightBoxHeaderAcura]: props.theme === 'acura',
            [css.LightBoxHeaderGenesis]: props.theme === 'genesis',
            [css.LightBoxHeaderAudi]: props.theme === THEME.AUDI,
        },
    );

    const NameStyles = classNames(
        css.Name,
        {
            [css.NameBMW]: props.theme === 'bmw',
            [css.NameMazda]: props.theme === 'mazda',
            [css.NameGenesis]: props.theme === 'genesis',
            [css.NameLexus]: props.theme === 'lexus',
        },
    );

    return (
        <div className={css.UserAcct}>
            {loadingProgress}
            <ScrollBar scrollVertical>
                <div className={css.UserAcctInner}>
                    <h2 className={NameStyles}>
                        {props.identityVerified ?
                            strings.formatString(strings.HiUser, firstName) :
                            strings.WelcomeBack}
                    </h2>
                    <h3>{strings.AccountSelect}</h3>
                    <div className={classNames(css.UserAccount,
                        { [css.UserAccountAudi]: props.theme === THEME.AUDI })}>
                        <div className={css.linkCellA}>
                            <UserAccountPanel id='appt' header={strings.ApptRepair} links={apptLinks}
                                clickHandler={clickHandler} theme={props.theme} />
                            <UserAccountPanel id='account' header={strings.Account} links={acctLinks}
                                clickHandler={clickHandler} theme={props.theme} />
                        </div>
                        <div className={css.linkCellB}>
                            <UserAccountPanel id='vehicle' header={strings.Vehicle} links={vehLinks}
                                clickHandler={clickHandler} theme={props.theme} />
                            <UserAccountPanel
                                id='contact' header={strings.ContactUs} links={dlrLinks}
                                clickHandler={clickHandler} theme={props.theme}
                            />
                        </div>
                    </div>
                </div>
            </ScrollBar>
            <Dialog
                cssClasses={{
                    content: classNames(css.content, { [css.contentAudi]: props.theme === THEME.AUDI }),
                    paper: classNames(css.paper, { [css.paperAudi]: props.theme === THEME.AUDI }),
                    header: lightBoxHeader,
                }}
                isOpen={isOpen}
                header={strings.ContactUs}
                hideCancel
                hideSave
                onCancel={closeHandler}
                width={1000}
                height={800}
                onBackDropClick={closeHandler}
            >
                <ContactDealer
                    custInfo={props.customerInfo}
                    specInfo={props.specInfo}
                    theme={props.theme}
                    address={props.address}
                    locale={props.locale}
                    vehicles={props.vehicles.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    identityVerified={props.identityVerified}
                />
            </Dialog>
        </div>
    );
};

export default UserAccountMenu;